/**
 * The three "how do we reach this account about billing" boxes: email, text
 * message, phone call.
 *
 * ONE definition, used at registration (both sign-up forms), on the family's
 * own profile, on the staff Preferences tab and in the Main contact form, so
 * the wording and the rule cannot drift between them.
 *
 * The rule (utils/accountNoticeMethods): at least one must stay on. The last
 * one that is on is shown ticked and locked, with a sentence saying why. A
 * method with no address to use (texts or calls with no phone number) says
 * so, and does not count.
 *
 * Presentation only: the caller holds the values and saves them. The server
 * enforces the same rule whatever a screen sends.
 */
import React from "react";
import { Checkbox } from "../ui";
import {
  ACCOUNT_NOTICE_METHODS,
  LAST_METHOD_NOTE,
  NEEDS_EMAIL_NOTE,
  NEEDS_PHONE_NOTE,
  isLastAccountNoticeMethod,
  methodLacksAddress,
  type AccountNoticeChoice,
  type AccountNoticeMethod,
  type ReachAddresses,
} from "../../utils/accountNoticeMethods";

export const ACCOUNT_NOTICE_LABELS: Record<AccountNoticeMethod, string> = {
  email: "Email",
  text: "Text message",
  call: "Phone call",
};

/**
 * The text-message disclosures ("Msg & data rates may apply", HELP / STOP)
 * are required next to a text opt-in by the carrier review. Keep them when
 * rewording.
 */
export const ACCOUNT_NOTICE_DESCRIPTIONS: Record<AccountNoticeMethod, string> = {
  email: "Invoices, receipts, payment problems, and past-due notices, by email.",
  text: "The same notices by text message. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to cancel.",
  call: "A staff member calls instead. There is no automated call.",
};

interface Props {
  value: AccountNoticeChoice;
  onToggle: (method: AccountNoticeMethod, checked: boolean) => void;
  /** Defaults to "there is an email address and a phone number". */
  addresses?: ReachAddresses;
  /** Boxes that cannot be changed for another reason, with the sentence to show. */
  locked?: Partial<Record<AccountNoticeMethod, string>>;
  /** Extra line under a box (for example "Not asked yet" or "On since …"). */
  notes?: Partial<Record<AccountNoticeMethod, string>>;
  /** Replace a box's description (the registration form names schedule changes too). */
  descriptions?: Partial<Record<AccountNoticeMethod, string>>;
  labels?: Partial<Record<AccountNoticeMethod, string>>;
  /** Everything disabled while a save is in flight. */
  busy?: boolean;
  className?: string;
}

const AccountNoticeMethodBoxes: React.FC<Props> = ({
  value,
  onToggle,
  addresses = { hasEmail: true, hasPhone: true },
  locked = {},
  notes = {},
  descriptions = {},
  labels = {},
  busy = false,
  className = "space-y-3",
}) => (
  <div className={className} data-testid="account-notice-methods">
    {ACCOUNT_NOTICE_METHODS.map((method) => {
      const checked = value[method];
      const last = isLastAccountNoticeMethod(method, value, addresses);
      const noAddress = methodLacksAddress(method, addresses);
      // Why this box is the way it is, most important reason first.
      const reasons: string[] = [];
      if (last) reasons.push(LAST_METHOD_NOTE);
      else if (locked[method]) reasons.push(locked[method] as string);
      if (noAddress) reasons.push(method === "email" ? NEEDS_EMAIL_NOTE : NEEDS_PHONE_NOTE);
      if (notes[method]) reasons.push(notes[method] as string);
      // With no address the box can still be turned OFF, never on.
      const disabled = busy || last || !!locked[method] || (noAddress && !checked);
      return (
        <Checkbox
          key={method}
          label={labels[method] || ACCOUNT_NOTICE_LABELS[method]}
          description={
            <>
              {descriptions[method] || ACCOUNT_NOTICE_DESCRIPTIONS[method]}
              {reasons.map((reason) => (
                <span key={reason} className="block mt-1" data-testid={`account-notice-${method}-note`}>
                  {reason}
                </span>
              ))}
            </>
          }
          checked={checked}
          disabled={disabled}
          onChange={(e) => {
            // A locked box changes nothing, however the click arrived.
            if (!disabled) onToggle(method, e.target.checked);
          }}
          data-testid={`account-notice-${method}`}
        />
      );
    })}
  </div>
);

export default AccountNoticeMethodBoxes;
